<template>
  <div class="bg-beige1 font-Spectral text-[14px] font-normal leading-[24px] text-[#423D36] md:text-[16px]">
    <div class="p-6">
      <div :class="['grid grid-cols-2 gap-4', { 'md:grid-cols-6': enablePcLayout }]">
        <div :class="['flex items-center gap-2', { 'md:flex-col md:gap-2 md:text-center': enablePcLayout }]">
          <img src="@/assets/icon/location/gray.svg" :class="['h-6 w-6', { 'md:h-12 md:w-12': enablePcLayout }]" />
          <NuxtLink v-if="props.enableDestinationLink && destinationHubPath" class="text-gold1 underline decoration-transparent underline-offset-4 transition-colors hover:decoration-gold4" :to="destinationHubPath">{{ destinationTagName }}</NuxtLink>
          <p v-else>{{ destinationTagName }}</p>
        </div>
        <div :class="['flex items-center gap-2', { 'md:flex-col md:gap-2 md:text-center': enablePcLayout }]">
          <img src="@/assets/icon/clock.png" :class="['h-6 w-6', { 'md:h-12 md:w-12': enablePcLayout }]" />
          <p>{{ experience.experienceDuration?.value }} {{ experience.experienceDuration?.unit }}</p>
        </div>
        <div v-if="formattedPrice" :class="['flex min-w-0 items-center gap-2', { 'md:flex-col md:gap-2 md:text-center': enablePcLayout }]">
          <img src="@/assets/icon/price.png" :class="['h-6 w-6 shrink-0', { 'md:h-12 md:w-12': enablePcLayout }]" />
          <!-- enablePcLayout=true: 画面幅でSP/PCを切り替え -->
          <template v-if="enablePcLayout">
            <!-- SP: from ¥30,000/person 形式、テキスト全体がクリック領域 -->
            <button v-if="hasPriceAnnotation" aria-label="Price Details" class="min-w-0 cursor-pointer break-words text-left md:hidden" type="button" @click="setPriceDrawerOpen(true)">
              {{ priceParts.amount
              }}<span class="whitespace-nowrap">{{ priceParts.chargeLabel }}<img alt="" class="ml-0.5 inline h-[14px] w-[14px] align-middle" src="@/assets/icon/info/gray.svg" /></span>
            </button>
            <p v-else class="min-w-0 break-words md:hidden">{{ formattedPrice }}</p>
            <!-- PC: from ¥30,000/person 形式、アイコンのみクリック領域 -->
            <p class="hidden items-center md:inline-flex">
              <span>{{ formattedPrice }}</span>
              <button v-if="hasPriceAnnotation" aria-label="Price Details" class="shrink-0 cursor-pointer p-[3px]" type="button" @click="setPriceDrawerOpen(true)">
                <img alt="" class="h-[18px] w-[18px]" src="@/assets/icon/info/gray.svg" />
              </button>
            </p>
          </template>
          <!-- enablePcLayout=false: 常にSP表示、from ¥30,000/person 形式、テキスト全体がクリック領域 -->
          <template v-else>
            <button v-if="hasPriceAnnotation" aria-label="Price Details" class="min-w-0 cursor-pointer break-words text-left" type="button" @click="setPriceDrawerOpen(true)">
              {{ priceParts.amount
              }}<span class="whitespace-nowrap">{{ priceParts.chargeLabel }}<img alt="" class="ml-0.5 inline h-[14px] w-[14px] align-middle" src="@/assets/icon/info/gray.svg" /></span>
            </button>
            <p v-else class="min-w-0 break-words">{{ formattedPrice }}</p>
          </template>
        </div>
        <div :class="['flex min-w-0 items-center gap-2', { 'md:flex-col md:gap-2 md:text-center': enablePcLayout }]">
          <img alt="" src="@/assets/icon/group.svg" :class="['h-6 w-6 shrink-0', { 'md:h-12 md:w-12': enablePcLayout }]" />
          <!-- enablePcLayout=true: 画面幅でSP/PCを切り替え -->
          <template v-if="enablePcLayout">
            <!-- SP: テキスト全体がクリック領域 -->
            <button v-if="hasGuestCompositionAnnotation" class="min-w-0 cursor-pointer break-words text-left md:hidden" type="button" :aria-label="`${guestRangeText}, Guest Details`" @click="setGuestDrawerOpen(true)">
              {{ guestRangeText }}<img alt="" class="ml-0.5 inline h-[14px] w-[14px] align-middle" src="@/assets/icon/info/gray.svg" />
            </button>
            <p v-else class="min-w-0 break-words md:hidden">{{ guestRangeText }}</p>
            <!-- PC: アイコンのみクリック領域 -->
            <p class="hidden items-center md:inline-flex">
              <span>{{ guestRangeText }}</span>
              <button v-if="hasGuestCompositionAnnotation" aria-label="Guest Details" class="shrink-0 cursor-pointer p-[3px]" type="button" @click="setGuestDrawerOpen(true)">
                <img alt="" class="h-[18px] w-[18px]" src="@/assets/icon/info/gray.svg" />
              </button>
            </p>
          </template>
          <!-- enablePcLayout=false: 常にSP表示、テキスト全体がクリック領域 -->
          <template v-else>
            <button v-if="hasGuestCompositionAnnotation" class="min-w-0 cursor-pointer break-words text-left" type="button" :aria-label="`${guestRangeText}, Guest Details`" @click="setGuestDrawerOpen(true)">
              {{ guestRangeText }}<img alt="" class="ml-0.5 inline h-[14px] w-[14px] align-middle" src="@/assets/icon/info/gray.svg" />
            </button>
            <p v-else class="min-w-0 break-words">{{ guestRangeText }}</p>
          </template>
        </div>
        <div :class="['flex items-center gap-2', { 'md:flex-col md:gap-2 md:text-center': enablePcLayout }]">
          <img src="@/assets/icon/record_voice_over.svg" :class="['h-6 w-6', { 'md:h-12 md:w-12': enablePcLayout }]" />
          <p>{{ interpreterOptionText }}</p>
        </div>
        <div :class="['flex items-center gap-2', { 'md:flex-col md:gap-2 md:text-center': enablePcLayout }]">
          <img src="@/assets/icon/event_available.svg" :class="['h-6 w-6', { 'md:h-12 md:w-12': enablePcLayout }]" />
          <p>{{ cancelPolicyText }}</p>
        </div>
      </div>
    </div>
  </div>
  <ClientOnly>
    <TitledRichTextDrawer title="Price Details" :content="experience.referencePriceAnnotation" :on-open-change="setPriceDrawerOpen" :open="isPriceDrawerOpen" />
    <TitledRichTextDrawer title="Guest Details" :content="experience.guestCompositionAnnotation" :on-open-change="setGuestDrawerOpen" :open="isGuestDrawerOpen" />
  </ClientOnly>
</template>

<script lang="ts" setup>
import type { WabunkaExperienceViewModel } from '@/models/wabunka/wabunkaExperience';
import { interpreterOptionText as buildInterpreterOptionText } from '@/models/wabunka/wabunkaInterpreterOption';
import { displayPrice, displayPriceParts } from '@/wabunka-src/features/Experience';
import { cancelPolicyText as buildCancelPolicyText } from '@/wabunka-src/features/Experience/models/cancellationPolicyTrigger';
import { displayDestinationTagName } from '@/wabunka-src/features/Experience/models/wabunkaExperienceTag';
import { buildDestinationHubPath } from '@/wabunka-src/features/Experience/models/wabunkaExperienceTagHubLink';
import TitledRichTextDrawer from '@/wabunka-src/features/Experience/pages/ExperienceDetail/components/pure/TitledRichTextDrawer/TitledRichTextDrawer.vue';

interface Props {
  experience: WabunkaExperienceViewModel;
  enablePcLayout?: boolean;
  enableDestinationLink?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  enablePcLayout: true,
  enableDestinationLink: false,
});

const formattedPrice = computed(() => displayPrice(props.experience));
// 画面幅が狭い時に「/person」とアイコンを一緒に改行させるため、金額と課金単位を分割して取得
const priceParts = computed(() => displayPriceParts(props.experience) ?? { amount: '', chargeLabel: '' });
const destinationTagName = computed(() => displayDestinationTagName(props.experience.tags));
const destinationHubPath = computed(() => buildDestinationHubPath(props.experience.tags));
const interpreterOptionText = computed(() => buildInterpreterOptionText(props.experience.isInterpreterNeeded, props.experience.interpreterOptions));
const cancelPolicyText = computed(() => buildCancelPolicyText(props.experience.cancellationPolicyTrigger, props.experience.refundableCancellationDaysForGuest));
const hasPriceAnnotation = computed(() => Boolean(props.experience.referencePriceAnnotation?.trim()));

const guestRangeText = computed(() => `Private: ${props.experience.minGuestsPerReservation} - ${props.experience.maxGuests}`);
const hasGuestCompositionAnnotation = computed(() => Boolean(props.experience.guestCompositionAnnotation?.trim()));

const isPriceDrawerOpen = ref(false);
const setPriceDrawerOpen = (v: boolean) => {
  isPriceDrawerOpen.value = v;
};

const isGuestDrawerOpen = ref(false);
const setGuestDrawerOpen = (v: boolean) => {
  isGuestDrawerOpen.value = v;
};
</script>
